import React from "react";
import { LegalPageShell } from "./Legal";

const DecorativeBlobs = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute" }}
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <g id="accessibility-blob-1" transform="translate(-100,-100)">
        <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
        <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
      </g>
      <g id="accessibility-blob-2" transform="translate(100,-100) scale(-1,1)">
        <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
        <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
      </g>
    </defs>
  </svg>
);

const HeroPattern = () => (
  <div className="hero-pattern" aria-hidden="true">
    <svg
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <svg x="6%" y="22%" overflow="visible">
        <use
          href="#accessibility-blob-1"
          fill="#FFA5BC"
          transform="scale(1.5)"
          opacity="0.16"
        />
      </svg>
      <svg x="84%" y="76%" overflow="visible">
        <use
          href="#accessibility-blob-2"
          fill="#B798F2"
          transform="scale(1.8)"
          opacity="0.14"
        />
      </svg>
      <svg x="50%" y="-15%" overflow="visible">
        <use
          href="#accessibility-blob-1"
          fill="#E8EBFF"
          transform="scale(1.4)"
          opacity="0.55"
        />
      </svg>
      <svg x="68%" y="58%" overflow="visible">
        <use
          href="#accessibility-blob-2"
          fill="#F3EC7A"
          transform="scale(0.85)"
          opacity="0.30"
        />
      </svg>
    </svg>
  </div>
);

const Accessibility = () => (
  <LegalPageShell id="accessibility">
    <main id="main-content">
      <DecorativeBlobs />

      <header className="hero">
        <HeroPattern />
        <div className="container">
          <span className="eyebrow">Accessibility Statement</span>
          <h1 className="hero-title">
            Built to be used, <em>by everyone</em>.
          </h1>
          <p className="hero-sub">
            <strong>We want every learner to get on with the learning,</strong>{" "}
            whatever device or assistive technology they use. Here is where we
            stand, and how to tell us when we fall short.
          </p>
          <div className="hero-meta" aria-label="Page metadata">
            <span>
              <strong>Aiming for:</strong> WCAG 2.1 AA
            </span>
            <span>
              <strong>Last reviewed:</strong> 13 June 2026
            </span>
          </div>
        </div>
      </header>

      <section className="tldr-wrap" aria-labelledby="tldr-title">
        <div className="narrow">
          <div className="tldr">
            <div className="label">If you only read one thing</div>
            <h2 id="tldr-title">
              The whole statement, <em>in five bullets</em>.
            </h2>
            <ul>
              <li>
                <strong>We aim to meet WCAG 2.1 AA,</strong> the widely used
                standard for accessible websites.
              </li>
              <li>
                <strong>
                  The site works with a keyboard and screen readers,
                </strong>{" "}
                scales when you zoom, and respects reduced-motion settings.
              </li>
              <li>
                <strong>We are not perfect.</strong> Some older course content
                and a few third-party bits still need work.
              </li>
              <li>
                <strong>Found a barrier?</strong> Tell us and we will fix it,
                and help you get what you needed in the meantime.
              </li>
              <li>
                <strong>Reach a human</strong> at{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>
                . We reply within 24 hours, often sooner.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <nav className="toc" aria-label="Jump to a section">
        <div className="container toc-inner">
          <span className="toc-label">Jump to:</span>
          <div className="toc-pills">
            <a href="#commitment" className="toc-pill">
              Our commitment
            </a>
            <a href="#standard" className="toc-pill">
              The standard
            </a>
            <a href="#works" className="toc-pill">
              What works well
            </a>
            <a href="#limits" className="toc-pill">
              Known limitations
            </a>
            <a href="#tech" className="toc-pill">
              Assistive tech
            </a>
            <a href="#help" className="toc-pill">
              Getting help
            </a>
            <a href="#enforcement" className="toc-pill">
              Enforcement
            </a>
            <a href="#contact" className="toc-pill">
              Contact
            </a>
          </div>
        </div>
      </nav>

      <section id="commitment" aria-labelledby="commitment-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">01</span>
            <h2 className="section-title" id="commitment-title">
              Our <em>commitment</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              Learning should be open to everyone. If our site gets in your way,
              that is our problem to fix, not yours to work around.
            </div>
          </div>
          <div className="prose">
            <p>
              42courses is used by people in more than 170 countries, on every
              kind of device, with every kind of need. We design and build with
              that in mind: clear structure, readable type, sensible colour
              contrast, and controls that work however you choose to drive them.
            </p>
            <p>
              Accessibility is not a box we tick once. We check it as we build
              new pages and revisit older ones, and we would genuinely rather
              hear from you than have you struggle in silence.
            </p>
          </div>
        </div>
      </section>

      <section
        id="standard"
        aria-labelledby="standard-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">02</span>
            <h2 className="section-title" id="standard-title">
              The standard <em>we aim for</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              We are working towards the{" "}
              <strong>
                Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA
              </strong>
              , the international benchmark most organisations use. We consider
              the site <strong>partially conformant</strong>: most of it meets
              the standard, and the parts that do not yet are listed under known
              limitations below.
            </p>
            <p>
              We treat AA as the floor, not the ceiling, and pick up AAA wins
              like generous contrast and reduced-motion support wherever they
              are sensible.
            </p>
          </div>
        </div>
      </section>

      <section id="works" aria-labelledby="works-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">03</span>
            <h2 className="section-title" id="works-title">
              What <em>works well</em>.
            </h2>
          </div>
          <div className="prose">
            <p>Across the site you can expect:</p>
          </div>
          <div className="rights-grid">
            <div className="right-card">
              <h4>Keyboard navigation</h4>
              <p>
                Every interactive control is reachable and usable with a
                keyboard alone, with a visible focus outline.
              </p>
            </div>
            <div className="right-card">
              <h4>Screen-reader support</h4>
              <p>
                Semantic headings, landmarks, labelled controls, and a skip to
                main content link.
              </p>
            </div>
            <div className="right-card">
              <h4>Readable contrast</h4>
              <p>
                Text and key controls are designed to meet or beat AA contrast
                ratios, including our buttons and links.
              </p>
            </div>
            <div className="right-card">
              <h4>Zoom and reflow</h4>
              <p>
                The site reflows cleanly when you zoom to 200% or use a small
                screen.
              </p>
            </div>
            <div className="right-card">
              <h4>Reduced motion</h4>
              <p>
                If your device asks for less motion, animations are dialled
                right down automatically.
              </p>
            </div>
            <div className="right-card">
              <h4>Captions and subtitles</h4>
              <p>
                Course videos carry subtitles, and lessons are built around text
                so the content stands on its own.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        id="limits"
        aria-labelledby="limits-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">04</span>
            <h2 className="section-title" id="limits-title">
              Known <em>limitations</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              Here is what we know is not perfect yet. We would rather tell you
              than pretend.
            </div>
          </div>
          <div className="prose">
            <ul>
              <li>
                <strong>Some older course content.</strong> A handful of earlier
                lessons use images or interactions that are not fully described
                yet.
              </li>
              <li>
                <strong>Third-party embeds.</strong> Parts we do not build
                ourselves, like payment windows or embedded video players,
                follow their own providers' accessibility.
              </li>
              <li>
                <strong>Subtitles are English-only.</strong> Course text is
                available in several languages, but video subtitles are
                currently English only.
              </li>
              <li>
                <strong>Some PDFs and certificates.</strong> Downloadable
                documents may not yet be fully tagged for screen readers.
              </li>
              <li>
                <strong>No native mobile app.</strong> The site is
                mobile-friendly, but there is no separate app and offline access
                is not supported yet.
              </li>
            </ul>
            <p>
              If any of these stop you doing what you came to do, contact us and
              we will sort out an accessible alternative.
            </p>
          </div>
        </div>
      </section>

      <section id="tech" aria-labelledby="tech-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">05</span>
            <h2 className="section-title" id="tech-title">
              Assistive technology <em>we support</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              The site is built to work with current versions of common tools,
              including:
            </p>
            <ul>
              <li>
                Screen readers such as VoiceOver, TalkBack, NVDA and JAWS.
              </li>
              <li>Browser zoom and operating-system text scaling.</li>
              <li>Keyboard-only and switch-style navigation.</li>
              <li>Speech-recognition input.</li>
            </ul>
            <p>
              We test against the latest two versions of Chrome, Safari, Firefox
              and Edge. If you use something older or more specialist and hit a
              wall, let us know what you are on and we will do our best to help.
            </p>
          </div>
        </div>
      </section>

      <section
        id="help"
        aria-labelledby="help-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">06</span>
            <h2 className="section-title" id="help-title">
              Getting help, <em>or telling us off</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              If something on the site is not working for you, email{" "}
              <a href="mailto:groundcontrol@42courses.com">
                groundcontrol@42courses.com
              </a>{" "}
              with <strong>"FAO: Accessibility"</strong> in the subject line. It
              helps if you can tell us:
            </p>
            <ul>
              <li>The page or course you were on.</li>
              <li>What you were trying to do.</li>
              <li>
                The device, browser, and any assistive technology you were
                using.
              </li>
            </ul>
            <p>
              We reply within 24 hours, often sooner, and we will help you get
              what you needed while we fix the underlying problem.
            </p>
          </div>
        </div>
      </section>

      <section id="enforcement" aria-labelledby="enforcement-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">07</span>
            <h2 className="section-title" id="enforcement-title">
              Enforcement and <em>the formal bit</em>.
            </h2>
          </div>
          <div className="prose">
            <p>
              42courses is a private company, so the UK's public-sector
              accessibility regulations do not strictly apply to us. We aim to
              meet WCAG 2.1 AA voluntarily because it is the right thing to do,
              not because a rule makes us.
            </p>
            <p>
              If you raise an accessibility problem and are not happy with how
              we respond, you can contact the{" "}
              <strong>Equality and Human Rights Commission (EHRC)</strong>,
              which oversees the Equality Act 2010 in Great Britain. Learners in
              the EU may also have rights under the European Accessibility Act.
            </p>
            <p>
              <em>
                This statement was prepared on 13 June 2026 and is reviewed
                regularly.
              </em>
            </p>
          </div>
        </div>
      </section>

      <section
        id="contact"
        className="still-got-q"
        aria-labelledby="contact-title"
      >
        <div className="narrow">
          <h2 id="contact-title">
            Hit a barrier? <em>Tell us.</em>
          </h2>
          <p>
            We will help you get what you need and use your report to improve
            the site for the next learner too.
          </p>
          <div className="btn-row">
            <a
              href="mailto:groundcontrol@42courses.com?subject=FAO:%20Accessibility"
              className="btn btn-primary"
            >
              Email Ground Control
            </a>
            <a href="/privacy" className="btn btn-secondary">
              Read our privacy policy
            </a>
          </div>
        </div>
      </section>

      <footer className="footer-egg">
        <div className="big">
          <em>Everyone in.</em>
        </div>
        <div className="small">Learning works better when the doors open.</div>
        <div className="version">Version: 13 June 2026</div>
      </footer>
    </main>
  </LegalPageShell>
);

export default Accessibility;
